import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/utils';
import { RecordLink } from './RecordLink';
import type { RecordType } from './registry';

/** A {type,id,label} reference as the by-id endpoints return it. type null = plain text. */
export interface RecordSource {
  type: string | null;
  id: string;
  label: string;
}

export const money = (v: number | string | null | undefined) => formatCurrency(Number(v ?? 0));
export const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '');
export const day = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '');

export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-end">{children}</dd>
    </div>
  );
}

export function Who({ label, name, at }: { label: string; name?: string | null; at?: string | null }) {
  if (!name && !at) return null;
  return (
    <Row label={label}>
      {name}
      {at && <span className="ms-2 text-xs text-muted-foreground" dir="ltr">{when(at)}</span>}
    </Row>
  );
}

export function SourceLink({ source }: { source: RecordSource }) {
  return source.type ? (
    <RecordLink type={source.type as RecordType} id={source.id} label={source.label} />
  ) : (
    <span>{source.label}</span>
  );
}

/** Footer 'Source' row of a body (links to the records this one came from). */
export function SourceRow({ items }: { items: Array<{ label: string; source: RecordSource | null | undefined }> }) {
  const { t } = useTranslation('records');
  const shown = items.filter((i) => i.source);
  if (shown.length === 0) return null;
  return (
    <div className="space-y-1.5 border-t pt-3" data-testid="record-viewer-source">
      <p className="text-xs font-medium text-muted-foreground">{t('source')}</p>
      <dl className="space-y-1.5">
        {shown.map((i) => (
          <Row key={i.label} label={i.label}>
            <SourceLink source={i.source as RecordSource} />
          </Row>
        ))}
      </dl>
    </div>
  );
}

export function Money({ value }: { value: number | string | null | undefined }) {
  return <span className="tabular-nums" dir="ltr">{money(value)}</span>;
}
